// frontend/src/lib/calendar-day-start.js
// Shared logic for the Calendar Hub's rotatable 24-hour time axis.
//
// The Day and Week views render a vertical axis of 24 hour rows. By default
// the axis starts at midnight, but a user can anchor it to any hour (stored
// as the per-user preference `ui_preferences.calendar_day_start_hour`). With a
// start hour of 06, the axis reads 06:00 at the top, runs through all 24
// hours, and ends at 06:00 again at the bottom.
//
// Day columns stay calendar-date based — only the *visual axis* rotates. An
// event whose interval crosses the chosen start hour (e.g. 05:00–07:00 with a
// 06:00 start) therefore straddles the top/bottom seam; `eventFragments`
// splits it into a bottom piece and a top piece so nothing is lost.

/** Axis start hour used when the user has expressed no preference. */
export const DEFAULT_DAY_START_HOUR = 6

const MINUTES_PER_DAY = 24 * 60

/**
 * Coerce an arbitrary value to a valid axis start hour (integer 0–23).
 * Anything missing, non-integer, or out of range falls back to the default.
 *
 * @param {*} h  raw value (number, numeric string, null, …)
 * @returns {number} integer in [0, 23]
 */
export function clampDayStartHour(h) {
  if (h === null || h === undefined || h === '') return DEFAULT_DAY_START_HOUR
  const n = Number(h)
  if (!Number.isInteger(n) || n < 0 || n > 23) return DEFAULT_DAY_START_HOUR
  return n
}

/**
 * Build the 24 hour rows of the axis, rotated to begin at `startHour`.
 *
 * @param {number} startHour  axis start hour
 * @returns {Array<{hour: number, label: string}>} 24 slots, top→bottom;
 *   `hour` is the clock hour (0–23), `label` is `"HH:00"`.
 */
export function buildHourSlots(startHour) {
  const start = clampDayStartHour(startHour)
  return Array.from({ length: 24 }, (_, i) => {
    const hour = (start + i) % 24
    return { hour, label: `${String(hour).padStart(2, '0')}:00` }
  })
}

/**
 * Minutes from the top of the rotated axis for a given Date.
 * 0 at exactly `startHour`:00; 1439 one minute before it comes round again.
 *
 * @param {Date} date
 * @param {number} startHour  axis start hour
 * @returns {number} integer in [0, 1439]
 */
export function rotatedMinuteOffset(date, startHour) {
  const start = clampDayStartHour(startHour)
  const minutes = date.getHours() * 60 + date.getMinutes()
  return (((minutes - start * 60) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
}

/**
 * Position an event on the rotated axis as one or more vertical fragments.
 *
 * Inputs are the event's start/end already clipped to the visible calendar
 * date. The normal case yields a single `{topPct, heightPct}`. When the
 * interval crosses the axis seam at `startHour` it yields two fragments — a
 * bottom piece running to the seam and a top piece resuming from it.
 *
 * @param {Date} clippedStart  event start, clipped to the day
 * @param {Date} clippedEnd    event end, clipped to the day
 * @param {number} startHour   axis start hour
 * @returns {Array<{topPct: number, heightPct: number}>} 1 or 2 fragments,
 *   each a percentage of the 24h axis height.
 */
export function eventFragments(clippedStart, clippedEnd, startHour) {
  const start = clampDayStartHour(startHour)
  const startOff = rotatedMinuteOffset(clippedStart, start)

  // Duration within a single calendar day. Guard a zero/negative span (a
  // point-in-time event) to a hairline, and cap at a full day.
  let durationMin = Math.round((clippedEnd.getTime() - clippedStart.getTime()) / 60000)
  if (durationMin <= 0) durationMin = 1
  if (durationMin > MINUTES_PER_DAY) durationMin = MINUTES_PER_DAY

  // Minimum rendered height so a very short event stays clickable.
  const MIN_PCT = 2
  const pct = (min) => (min / MINUTES_PER_DAY) * 100
  const endOff = startOff + durationMin

  if (endOff <= MINUTES_PER_DAY) {
    return [{ topPct: pct(startOff), heightPct: Math.max(MIN_PCT, pct(durationMin)) }]
  }

  // Crosses the seam: bottom piece (startOff → axis end) + top piece.
  const bottomMin = MINUTES_PER_DAY - startOff
  const topMin = endOff - MINUTES_PER_DAY
  const fragments = [
    { topPct: pct(startOff), heightPct: Math.max(MIN_PCT, pct(bottomMin)) },
  ]
  if (topMin > 0) {
    fragments.push({ topPct: 0, heightPct: Math.max(MIN_PCT, pct(topMin)) })
  }
  return fragments
}
